import { getTimezone } from 'reducers/utils';
import { select, put, call, takeLatest } from '@redux-saga/core/effects';
import API from 'services/api';
import {
  eventsFilterActions,
  eventsGridActions,
  eventsNS,
  eventsPageActions,
  eventSelectors,
  followupEventActions,
  followupEventNS,
} from 'containers/Pages/Events';
import { setLoading, showSnackbar, toggleModal } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import get from 'lodash/get';
import { t } from 'i18next';
import { getUserRoles, getUserSelectedLanguage } from 'reducers/users';
import { ROLES } from 'utils/constants';
import { omit } from 'utils/methods/helpers';
import { createSimpleTask } from 'sagas/templateSaga/templateSaga';
import isEmpty from 'lodash/isEmpty';
export function* printEventDetailsSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchEventDetailsPdf, {
      id,
      timezone,
      language: language.toUpperCase(),
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* fetchEventsList({ payload }) {
  try {
    const { filters, from } = payload;
    const timezone = yield select(getTimezone);
    const filterKeyStatus = yield select(eventSelectors.getEventFilterKey);
    const forceRefresh = yield select(eventSelectors.getForceRefresh);
    const getEventList = yield select(eventSelectors.getEventListRaw);
    // if firebase sends a notification or workOrderList is empty or filter key is pressed or pagination is increased
    if (
      isEmpty(getEventList) ||
      forceRefresh ||
      filterKeyStatus ||
      (from === 0 && !isEmpty(getEventList)) ||
      (from > 0 && !isEmpty(getEventList))
    ) {
      const { data } = yield call(API.fetchEventList, {
        ...filters,
        from,
        timezone,
      });
      if (data.data != null) {
        const totalEntries = get(data, 'meta.total');
        yield put(
          eventsGridActions.setGridList({
            list: data.data,
            totalEntries,
          }),
        );
      }
    }
    if (filterKeyStatus) {
      yield put(eventsFilterActions.setFilterKey(false));
    }
    if (forceRefresh) {
      yield put(eventsGridActions.setForceRefresh(false));
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchEventsDetailsSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id, eventType } = payload;
    const { data } = yield call(API.fetchEventDetails, id);
    // check if root event is open
    const rootevent = yield select(eventSelectors.getEventDetailsRaw);
    // check if followup event is open
    const followupEvent = yield select(eventSelectors.getFollowupEventDetails);

    // there is no event type and followupEvent is empty
    const onlyRootIsOpen =
      !eventType && Object.keys(followupEvent).length === 0;
    // there is event type, followupEvent and root is not empty
    const rootAndFollowupIsOpen =
      Object.keys(followupEvent).length > 0 &&
      Object.keys(rootevent).length > 0;
    //-----------------

    // set followup event details
    if (
      data.data != null &&
      (rootAndFollowupIsOpen || (eventType && eventType === followupEventNS))
    ) {
      // set followup event details
      yield put(
        followupEventActions.setFollowupEventDetails({
          followupEventDetails: data.data,
        }),
      );
    }
    // parent event details is only opened
    if (data.data != null && onlyRootIsOpen) {
      // set event details
      yield put(
        eventsGridActions.setGridRowDetails({
          details: data.data,
        }),
      );
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* assignEventToPersonnelSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id, selectedAssignee } = payload;
    const { firstName, lastName } = selectedAssignee;
    yield call(API.assignEventToPersonnel, id, selectedAssignee);
    yield call(fetchEventsDetailsSaga, { payload: { id } });
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'success',
        message: `${t('Event assigned to')} ${firstName} ${lastName}`,
      }),
    );
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* setEventInappropriateSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { eventId, reason, description } = payload;
    yield call(API.setInappropriate, eventId, reason, description);
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* approveEventSaga({ payload }) {
  try {
    const { approver, id } = payload;
    yield put(setLoading(true));
    yield call(API.approveEvent, approver, id);
    yield call(fetchEventsDetailsSaga, { payload: { id } });
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'success',
        message: 'Event published',
      }),
    );
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* rejectEventSaga({ payload }) {
  try {
    const { approver, id } = payload;
    yield put(setLoading(true));
    yield call(API.rejectEvent, approver, id);
    yield call(fetchEventsDetailsSaga, { payload: { id } });
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'success',
        message: 'Event reject',
      }),
    );
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* updateDistributionSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id, distribution } = payload;
    const userRoles = yield select(getUserRoles);
    yield call(API.updateEventDistribution, id, distribution);
    if (!userRoles.includes(ROLES.SUPERVISOR.id)) {
      yield call(fetchEventsDetailsSaga, { payload: { id } });
    }
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'success',
        message: `${t('Distribution set to')} ${distribution.toLowerCase()}`,
      }),
    );
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* createEventSaga({ payload }) {
  try {
    yield put(setLoading(true));

    const { event } = payload;
    const { media, ...rest } = event;
    const { data } = yield call(API.addEvent, rest);
    const { id } = data.data.general;
    for (const i in media) {
      if (Object.prototype.hasOwnProperty.call(media, i)) {
        yield call(API.uploadMedia, media[i], id);
      }
    }
    // if it is a followup form then refresh its parent details
    if (event.parentEventId) {
      yield call(fetchEventsDetailsSaga, {
        payload: { id: event.parentEventId },
      });
    }
    // only toggle if the form component is parent
    if (!event.parentEventId) {
      yield put(toggleModal(eventsNS));
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* deleteFollowUpEventsSaga({ payload }) {
  const { deletedfollowupEvents } = payload;

  for (const i in deletedfollowupEvents) {
    if (Object.prototype.hasOwnProperty.call(deletedfollowupEvents, i)) {
      const rest = omit(deletedfollowupEvents[i], ['media']);
      const archivedEvent = {
        general: {
          ...rest.general,
          archived: rest.archived,
        },
        locations: rest?.locations,
        operational: rest?.operational,
        publishing: rest?.publishing,
        details: rest?.details,
      };
      yield call(API.updateEvent, archivedEvent.general.id, archivedEvent);
    }
  }
}
export function* updateEventSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const {
      id,
      event,
      eventType,
      deletedfollowupEvents,
      deletedfollowUptask,
    } = payload;
    // archive if the deletedfollowUptask array has values
    if (deletedfollowUptask && deletedfollowUptask.length > 0) {
      for (const i in deletedfollowUptask) {
        if (Object.prototype.hasOwnProperty.call(deletedfollowUptask, i)) {
          const taskElement = deletedfollowUptask[i];
          const archivedTasks = {
            archived: taskElement.archived,
            id: taskElement.rootTaskId,
          };
          yield call(createSimpleTask, {
            data: {
              tasks: [archivedTasks],
            },
            rootTaskId: taskElement.rootTaskId,
          });
        }
      }
    }
    if (deletedfollowupEvents && deletedfollowupEvents.length > 0) {
      yield call(deleteFollowUpEventsSaga, {
        payload: { deletedfollowupEvents },
      });
    }
    const { media, ...rest } = event;
    // update the event
    yield call(API.updateEvent, id, rest);
    for (const i in media) {
      if (Object.prototype.hasOwnProperty.call(media, i)) {
        yield call(API.uploadMedia, media[i], id);
      }
    }
    if (eventType !== followupEventNS) {
      yield put(toggleModal(eventsNS));
    }

    yield call(fetchEventsDetailsSaga, { payload: { id } });
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* linkFollowupTaskIdToEvents({ payload }) {
  const { rootTaskId } = payload;
  // check if root event is open
  const rootevent = yield select(eventSelectors.getEventDetailsRaw);
  // check if followup event is open
  const followupEvent = yield select(eventSelectors.getFollowupEventDetails);

  const onlyRootIsOpen = Object.keys(followupEvent).length === 0;
  //  followupEvent and root is not empty
  const rootAndFollowupIsOpen =
    Object.keys(followupEvent).length > 0 && Object.keys(rootevent).length > 0;
  //-----------------
  let eventPayoad = {};
  let eventId = '';
  if (onlyRootIsOpen) {
    // updated root event
    eventPayoad = {
      ...rootevent,
      followupTasks: rootevent.followupTasks
        ? rootevent.followupTasks.concat([{ id: rootTaskId }])
        : [{ id: rootTaskId }],
    };
    eventId = rootevent.general.id;
  } else if (rootAndFollowupIsOpen) {
    // updated followup event
    eventPayoad = {
      ...followupEvent,
      parentEventId: followupEvent.parentEventID,
      followupTasks: followupEvent.followupTasks
        ? followupEvent.followupTasks.concat([{ id: rootTaskId }])
        : [{ id: rootTaskId }],
    };
    eventId = followupEvent.general.id;
  }

  yield call(updateEventSaga, {
    payload: {
      id: eventId,
      event: eventPayoad,
      eventType: followupEventNS,
    },
  });
}
export function* printEventListSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { filters } = payload;
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchEventListPdf, {
      ...filters,
      timezone,
      language: language.toUpperCase(),
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* eventsSagas() {
  yield takeLatest(eventsGridActions.fetchGridList, fetchEventsList);
  yield takeLatest(
    eventsGridActions.fetchGridRowDetails,
    fetchEventsDetailsSaga,
  );
  yield takeLatest(
    eventsPageActions.assignEventToPersonnel,
    assignEventToPersonnelSaga,
  );
  yield takeLatest(
    eventsPageActions.setInappropriate,
    setEventInappropriateSaga,
  );
  yield takeLatest(eventsPageActions.approveEvent, approveEventSaga);
  yield takeLatest(eventsPageActions.rejectEvent, rejectEventSaga);
  yield takeLatest(
    eventsPageActions.updateDistribution,
    updateDistributionSaga,
  );
  yield takeLatest(eventsGridActions.createRowEntry, createEventSaga);
  yield takeLatest(eventsGridActions.updateRowEntry, updateEventSaga);
  yield takeLatest(
    followupEventActions.fetchFollowupEventDetails,
    fetchEventsDetailsSaga,
  );
  yield takeLatest(eventsPageActions.printEvent, printEventDetailsSaga);
  yield takeLatest(eventsPageActions.printEventList, printEventListSaga);
}
